import React, { useState, Fragment } from 'react';
import { Grid, Menu, MenuItem, Typography, IconButton } from '@material-ui/core';
import { useGetTransferActions } from '~/hooks/transfers';
import { usePermissions } from 'react-admin';
import { useTranslate } from 'react-admin';
import { isEmpty } from 'lodash';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import Alert from '@material-ui/lab/Alert';
import { ConfirmationBase } from '~/components';
import { useSkip, useSubmit } from '~/hooks/transfers';
import { crudGetOne } from 'react-admin';
import { useDispatch } from 'react-redux';
import { Transfer } from '~/types/Transfer';
import { colors } from '@material-ui/core';

interface Props {
  record: Transfer;
}

const TransferActions = (props: Props) => {
  const { record } = props;
  const translate = useTranslate();
  const dispatch = useDispatch();

  const t = (key: string, args?: any): string =>
    translate(`resources.transfers.actions.${key}`, args);

  const { permissions } = usePermissions();
  const hasActionsPermissions = permissions?.includes('transaction.transfers.ledgerMapping.action');

  const { data: actions, refetch: refetchActions } = useGetTransferActions(
    record.id,
    hasActionsPermissions
  );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedSystem, setSelectedSystem] = useState<string>('');
  const [actionType, setActionType] = useState<string>('');
  const [confirmationOpen, setConfirmationOpen] = useState(false);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenu = (system: string, action: string) => {
    setSelectedSystem(system);
    setActionType(action);
    setConfirmationOpen(true);
    handleMenuClose();
  };

  const handleClose = () => {
    setConfirmationOpen(false);
    setTimeout(() => {
      setSelectedSystem('');
      setActionType('');
    }, 300);
  };

  const handleSuccess = () => {
    handleClose();
    dispatch(crudGetOne('transaction/transfers', record?.id, '/transaction/transfers'));
    refetchActions();
  };

  const skip = useSkip({
    onSuccess: handleSuccess,
    transferId: record?.id,
    system: selectedSystem,
  });

  const submit = useSubmit({
    onSuccess: handleSuccess,
    transferId: record?.id,
    system: selectedSystem,
  });

  const confirm = (type: string) => {
    if (selectedSystem) {
      type === 'skip' ? skip.mutateAsync() : submit.mutateAsync();
    }
  };
  if (!record || !actions || isEmpty(actions) || !hasActionsPermissions) return null;

  const systems = actions
    ? Object.keys(actions).filter((system) => {
        const systemActions = actions[system];
        return Object.values(systemActions).some((value) => value === true);
      })
    : [];

  if (systems.length === 0) return null;

  return (
    <>
      <IconButton
        aria-label='more'
        aria-controls='actions-menu'
        aria-haspopup='true'
        onClick={handleMenuOpen}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        {systems.map((system) => {
          const systemActions = actions[system];
          return (
            <Fragment key={`system-${system}`}>
              <Typography
                variant='subtitle2'
                style={{
                  padding: '8px 16px',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  color: colors.grey[500],
                }}
              >
                {system}
              </Typography>
              {systemActions.skip && (
                <MenuItem onClick={() => handleMenu(system, 'skip')}>{t('skip')}</MenuItem>
              )}
              {systemActions.submit && (
                <MenuItem onClick={() => handleMenu(system, 'submit')}>{t('submit')}</MenuItem>
              )}
            </Fragment>
          );
        })}
      </Menu>
      <ConfirmationBase
        open={confirmationOpen}
        onConfirm={() => confirm(actionType)}
        handleClose={handleClose}
        confirmationSettings={{
          variant: 'modal',
          modal: {
            heading: t('attention'),
            content: (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Alert severity='warning'>
                    {actionType &&
                      selectedSystem &&
                      t(`${actionType}Alert`, { system: selectedSystem })}
                  </Alert>
                </Grid>
              </Grid>
            ),
          },
        }}
      />
    </>
  );
};

export default TransferActions;
